import React, { useEffect, useId, useState } from 'react';
import SlidePanel from '../SlidePanel';
import JiraKeyChip from '../JiraKeyChip';
import InfoTip from '../InfoTip';
import ProgressBar from '../planning/ProgressBar';
import { StatusPill } from './PlanTable';
import { apiService } from '../../services/api';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useToast } from '../../contexts/ToastContext';
import * as fmt from '../../lib/format';
import { NOTE_FIELDS, PRIORITIES, PlanNote, PlanRow } from './types';

interface Props {
  row: PlanRow | null;
  /** May this person change this row. */
  canEdit: boolean;
  onClose: () => void;
  /** The plan changed (a note saved, an idea attached or dropped): read it again. */
  onChanged: () => void;
}

const FIELD = 'mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100';
const LABEL = 'block text-xs font-semibold uppercase tracking-wider text-gray-600';
const BUTTON = 'inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-medium transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50';

type Draft = Record<string, string>;

const draftOf = (note: PlanNote): Draft => ({
  priority: note.priority ?? '',
  ...Object.fromEntries(NOTE_FIELDS.map((f) => [f.key, (note[f.key] as string | null) ?? ''])),
});

export default function PlanRowPanel({ row, canEdit, onClose, onChanged }: Props) {
  const uid = useId();
  const confirm = useConfirm();
  const { showToast } = useToast();
  const [draft, setDraft] = useState<Draft>({});
  const [saving, setSaving] = useState(false);
  const [attachKey, setAttachKey] = useState('');
  const [busy, setBusy] = useState(false);

  // A different row, or the same row read again, starts from what is saved.
  useEffect(() => {
    if (row) setDraft(draftOf(row.note));
    setAttachKey('');
  }, [row]);

  const changed = row ? Object.entries(draftOf(row.note)).filter(([k, v]) => (draft[k] ?? '') !== v) : [];

  const save = async () => {
    if (!row || changed.length === 0) return;
    setSaving(true);
    try {
      const body = Object.fromEntries(changed.map(([k]) => [k, draft[k] === '' ? null : draft[k]]));
      const r = row.idea_id !== null ? await apiService.updatePlanIdea(row.idea_id, body) : await apiService.updatePlanNote(row.issue_key as string, body);
      if (!r.success) throw new Error(r.error);
      showToast('success', 'Saved');
      onChanged();
    } catch (e: any) {
      showToast('error', e?.response?.data?.error || e?.message || 'Could not save the notes');
    } finally {
      setSaving(false);
    }
  };

  const attach = async () => {
    if (!row || row.idea_id === null || !attachKey.trim()) return;
    setBusy(true);
    try {
      const r = await apiService.attachPlanIdea(row.idea_id, attachKey.trim());
      if (!r.success) throw new Error(r.error);
      showToast('success', `Attached to ${r.data.issue_key}`);
      onChanged();
      onClose();
    } catch (e: any) {
      showToast('error', e?.response?.data?.error || e?.message || 'Could not attach the ticket');
    } finally {
      setBusy(false);
    }
  };

  const drop = async () => {
    if (!row || row.idea_id === null) return;
    const yes = await confirm({
      title: 'Drop this idea?',
      message: 'It leaves the plan. It is archived, not deleted, so it is never lost.',
      confirmLabel: 'Drop idea',
    });
    if (!yes) return;
    setBusy(true);
    try {
      const r = await apiService.dropPlanIdea(row.idea_id);
      if (!r.success) throw new Error(r.error);
      showToast('success', 'Idea dropped');
      onChanged();
      onClose();
    } catch (e: any) {
      showToast('error', e?.response?.data?.error || e?.message || 'Could not drop the idea');
    } finally {
      setBusy(false);
    }
  };

  const p = row?.progress;
  return (
    <SlidePanel isOpen={!!row} onClose={onClose} title={row?.title ?? 'Plan item'} widthPercent={38}>
      {row && (
        <div className="space-y-5 p-5">
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill row={row} />
            {row.issue_key && row.jira_url && <JiraKeyChip issueKey={row.issue_key} url={row.jira_url} />}
            {row.idea_id !== null && (
              <span className="inline-flex items-center text-xs text-gray-600">
                No ticket yet<InfoTip id="plan.idea" />
              </span>
            )}
          </div>

          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
            {[
              ['Project', row.project_name],
              ['Component', row.component],
              ['Owner', row.owner],
              ['Asked by', row.requested_by],
              ['Updated', row.updated_at ? fmt.date(row.updated_at) : null],
            ]
              .filter(([, v]) => v)
              .map(([k, v]) => (
                <React.Fragment key={k}>
                  <dt className="text-gray-600">{k}</dt>
                  <dd className="min-w-0 break-words text-gray-900">{v}</dd>
                </React.Fragment>
              ))}
          </dl>

          {p && (
            <section aria-label="Progress of the tickets">
              <h3 className={LABEL}>Tickets inside</h3>
              <div className="mt-2">
                <ProgressBar shipped={p.shipped} ready={p.ready} open={p.open} invalid={p.invalid} />
              </div>
              <p className="mt-1.5 text-sm text-gray-700">
                {p.tickets === 0
                  ? 'No tickets yet.'
                  : `${fmt.percent(p.done_or_ready_pct)} of ${fmt.count(p.tickets - p.invalid)} tickets shipped or release-ready, ${fmt.count(p.open)} still open.`}
              </p>
            </section>
          )}

          {row.roadmap.length > 0 && (
            <section aria-label="Roadmap items">
              <h3 className={LABEL}>Roadmap items that point here</h3>
              <ul className="mt-1.5 space-y-1 text-sm text-gray-800">
                {row.roadmap.map((r) => (
                  <li key={r.key} className="break-words">
                    <span className="font-medium">{r.key}</span> {r.title}
                    {r.status ? <span className="text-gray-600"> · {r.status}</span> : null}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section aria-label="Notes" className="space-y-3">
            <h3 className={LABEL}>Notes from the planning sheet</h3>
            {canEdit ? (
              <>
                <div>
                  <div className="flex items-center">
                    <label htmlFor={`${uid}-priority`} className="text-sm font-medium text-gray-800">Priority</label>
                    <InfoTip id="plan.priority" />
                  </div>
                  <select id={`${uid}-priority`} value={draft.priority ?? ''} onChange={(e) => setDraft({ ...draft, priority: e.target.value })} className={FIELD}>
                    <option value="">Not set</option>
                    {PRIORITIES.map((x) => (
                      <option key={x} value={x}>{x}</option>
                    ))}
                  </select>
                </div>
                {NOTE_FIELDS.map((f) => (
                  <div key={f.key}>
                    <label htmlFor={`${uid}-${f.key}`} className="text-sm font-medium text-gray-800">{f.label}</label>
                    {f.long ? (
                      <textarea id={`${uid}-${f.key}`} rows={3} maxLength={2000} value={draft[f.key] ?? ''} onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })} className={FIELD} />
                    ) : (
                      <input id={`${uid}-${f.key}`} type="text" maxLength={2000} value={draft[f.key] ?? ''} onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })} className={FIELD} />
                    )}
                  </div>
                ))}
                <div className="flex justify-end">
                  <button type="button" onClick={save} disabled={saving || changed.length === 0} className={`${BUTTON} bg-blue-700 text-white hover:bg-blue-800 active:scale-[0.98]`}>
                    {saving ? 'Saving…' : 'Save notes'}
                  </button>
                </div>
              </>
            ) : (
              <ReadOnlyNotes note={row.note} />
            )}
          </section>

          {row.idea_id !== null && canEdit && (
            <section aria-label="Ticket for this idea" className="space-y-2 border-t border-gray-200 pt-4">
              <h3 className={LABEL}>Made a ticket for it?</h3>
              <p className="text-sm text-gray-700">Attach it by its key. The idea's notes move to the ticket, and the idea leaves this list.</p>
              <div className="flex flex-wrap items-end gap-2">
                <div className="min-w-[10rem] flex-1">
                  <label htmlFor={`${uid}-attach`} className="text-sm font-medium text-gray-800">Ticket key</label>
                  <input id={`${uid}-attach`} type="text" value={attachKey} onChange={(e) => setAttachKey(e.target.value)} placeholder="SRL-2856" className={FIELD} />
                </div>
                <button type="button" onClick={attach} disabled={busy || !attachKey.trim()} className={`${BUTTON} border border-gray-300 bg-white text-gray-800 hover:bg-gray-50`}>
                  Attach ticket
                </button>
              </div>
              <div className="pt-2">
                <button type="button" onClick={drop} disabled={busy} className={`${BUTTON} border border-rose-200 bg-white text-rose-700 hover:bg-rose-50`}>
                  Drop this idea
                </button>
              </div>
            </section>
          )}
        </div>
      )}
    </SlidePanel>
  );
}

function ReadOnlyNotes({ note }: { note: PlanNote }) {
  const filled = NOTE_FIELDS.filter((f) => note[f.key]);
  if (!note.priority && filled.length === 0) return <p className="text-sm text-gray-600">No notes yet. A leader, an admin or the project owner adds them.</p>;
  return (
    <dl className="space-y-2 text-sm">
      {note.priority && (
        <div>
          <dt className="text-gray-600">Priority</dt>
          <dd className="text-gray-900">{note.priority}</dd>
        </div>
      )}
      {filled.map((f) => (
        <div key={f.key}>
          <dt className="text-gray-600">{f.label}</dt>
          <dd className="whitespace-pre-wrap break-words text-gray-900">{note[f.key] as string}</dd>
        </div>
      ))}
    </dl>
  );
}
